import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "../../../lib/api/client"
import { extractArray, extractData, transformPaginatedResponse } from "../../../lib/api/response-utils"
import { timeEntriesKeys, billingKeys } from "../../../lib/query-keys"
import { toast } from "sonner"
import type {
  TimeEntry,
  TimeEntrySearchParams,
  CreateTimeEntryRequest,
  UpdateTimeEntryRequest,
  ApproveEntriesRequest,
  ImportToInvoiceRequest,
  BatchInvoiceRequest,
  BudgetSummary,
} from "../types"

const ITEMS_PER_PAGE = 20

// ── Queries ──

export function useTimeEntrySearch(params: TimeEntrySearchParams) {
  return useQuery({
    queryKey: timeEntriesKeys.list(params),
    queryFn: () => api.timeEntries.search(params),
    enabled: !!params.division || !!params.matter,
    select: (response) => {
      const { items, total, totalPages } = transformPaginatedResponse<TimeEntry>(
        response,
        ITEMS_PER_PAGE
      )
      return { entries: items, total, totalPages }
    },
  })
}

export function useTimeEntry(id: string | undefined) {
  return useQuery({
    queryKey: timeEntriesKeys.detail(id || ""),
    queryFn: async () => {
      if (!id) throw new Error("ID is required")
      const response = await api.timeEntries.findById(id)
      return extractData<TimeEntry>(response)
    },
    enabled: !!id,
  })
}

export function useTimeEntriesByMatter(matterId: string | undefined) {
  return useQuery({
    queryKey: timeEntriesKeys.matter(matterId || ""),
    queryFn: async () => {
      if (!matterId) throw new Error("Matter ID is required")
      const response = await api.timeEntries.findByMatter(matterId)
      const data = response.data as {
        entries: TimeEntry[]
        budget_summary?: BudgetSummary
      }
      return data
    },
    enabled: !!matterId,
  })
}

// ── Mutations ──

export function useCreateTimeEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTimeEntryRequest) => api.timeEntries.create(data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: timeEntriesKeys.matter(variables.matter),
      })
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.mySummary() })
      // When creating for another user, invalidate all timesheet queries
      if (variables.user) {
        queryClient.invalidateQueries({ queryKey: [...timeEntriesKeys.all, "timesheet"] })
      }
    },
  })
}

export function useUpdateTimeEntry(matterId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryRequest }) =>
      api.timeEntries.update(id, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() })
      if (matterId) {
        queryClient.invalidateQueries({ queryKey: timeEntriesKeys.matter(matterId) })
      }
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.mySummary() })
      // When user is reassigned, invalidate all timesheet queries (old + new user)
      if (variables.data.user) {
        queryClient.invalidateQueries({ queryKey: [...timeEntriesKeys.all, "timesheet"] })
      }
    },
  })
}

export function useDeleteTimeEntry(matterId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.timeEntries.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() })
      if (matterId) {
        queryClient.invalidateQueries({ queryKey: timeEntriesKeys.matter(matterId) })
      }
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.mySummary() })
    },
  })
}

export function useApproveTimeEntries(matterId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ApproveEntriesRequest) => api.timeEntries.approve(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() })
      if (matterId) {
        queryClient.invalidateQueries({ queryKey: timeEntriesKeys.matter(matterId) })
      }
    },
  })
}

export function useImportToInvoice(matterId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: ImportToInvoiceRequest) => api.timeEntries.importToInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.matter(matterId) })
      queryClient.invalidateQueries({ queryKey: billingKeys.matter(matterId) })
    },
  })
}

export function useBatchInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BatchInvoiceRequest) => api.timeEntries.batchInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.all })
    },
  })
}

export function useCombineTimeEntries(matterId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { time_entry_ids: string[]; billing_category: string; description?: string; billed_minutes?: number; date?: string; billable?: boolean; approve?: boolean; time_entry_template?: string }) =>
      api.timeEntries.combineEntries(data),
    onSuccess: async () => {
      toast.success("Entries merged and approved")
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() }),
        matterId
          ? queryClient.invalidateQueries({ queryKey: timeEntriesKeys.matter(matterId) })
          : Promise.resolve(),
        queryClient.invalidateQueries({ queryKey: timeEntriesKeys.mySummary() }),
      ])
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message?.[0] || "Failed to combine entries"
      toast.error(message)
    },
  })
}

export function useRemoveFromInvoice(matterId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { time_entry_ids: string[]; invoice_id: string }) =>
      api.timeEntries.removeFromInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.lists() })
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.matter(matterId) })
      queryClient.invalidateQueries({ queryKey: billingKeys.matter(matterId) })
    },
  })
}
